// RD-PERF-003 — memory-only stale-while-revalidate store for the dashboard
// "Recent activity" widget. Keyed `${tenantId}:${userId}:${profile}:${limit}` so one
// session's rows can never be read under another tenant/user. Never persisted;
// cleared at sign-out (clearPerUserDeviceState + clearSignedOutMemoryState).
import { fetchDashboardActivity } from '../../services/dashboardActivity';
import { DASHBOARD_ACTIVITY_WIDGET_LIMIT } from '../activity/dashboardRecentActivity';

const store = new Map(); // key -> { events, at }
const resolved = new Set();
const inflight = new Map();
const listeners = new Set();

const keyOf = ({ tenantId, userId, profile, limit = DASHBOARD_ACTIVITY_WIDGET_LIMIT }) => (
  `${tenantId}:${userId}:${profile}:${limit}`
);

// Bumped by clearDashboardActivityStore: a request that started before a
// sign-out drops its write instead of repopulating the store.
let epoch = 0;
const valid = (p) => Boolean(p && p.tenantId && p.userId && p.profile);

function notify() {
  [...listeners].forEach((fn) => { try { fn(); } catch (_) { /* never break a reader */ } });
}

/** Never rejects. On error keeps the previous value and still marks resolved. */
export function prefetchDashboardActivity({ tenantId, userId, profile, limit = DASHBOARD_ACTIVITY_WIDGET_LIMIT }) {
  if (!valid({ tenantId, userId, profile })) return Promise.resolve();
  const key = keyOf({ tenantId, userId, profile, limit });
  if (inflight.has(key)) return inflight.get(key);
  const startEpoch = epoch;
  const p = (async () => {
    try {
      const { data, error } = await fetchDashboardActivity({ tenantId, profile, limit });
      if (!error && startEpoch === epoch) store.set(key, { events: data || [], at: Date.now() });
    } catch (_) { /* keep previous value */ }
    if (startEpoch !== epoch) return; // signed out meanwhile — write nothing
    resolved.add(key);
    inflight.delete(key);
    notify();
  })();
  inflight.set(key, p);
  return p;
}

/** Last known events (any age) or null. */
export function peekDashboardActivity(params) {
  if (!valid(params)) return null;
  return store.get(keyOf(params))?.events ?? null;
}

export function isDashboardActivityResolved(params) {
  return valid(params) && resolved.has(keyOf(params));
}

export function subscribeDashboardActivity(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function clearDashboardActivityStore() {
  epoch += 1;
  store.clear();
  resolved.clear();
  inflight.clear();
  notify();
}
